import { isNil } from 'lodash';
import { UserImage } from '@shared/components/Image';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { API } from '@shared/types/api';
import { formatDateTime } from '@shared/util/date';
import { useIsDesktop } from '@shared/util/hooks';
import classNames from './InputUserAndDate.module.css';

interface Props {
  user?: API.ShortUserFragment;
  date?: string;
  indent?: boolean;
  collapsed?: boolean;
}

/**
 * This component displays a user and date in a compact or expanded format. This
 * is used to show metadata below other components like the GeolocationField or
 * SignatureField.
 */

export function InputUserAndDate({ user, date, indent, collapsed }: Props) {
  if (isNil(user) && isNil(date)) {
    return null;
  }

  if (collapsed) {
    return <UserAndDateCollapsed user={user} date={date} />;
  } else {
    return <UserAndDateExpanded user={user} date={date} indent={indent} />;
  }
}

function UserAndDateCollapsed({ user, date }: Props) {
  return (
    <View fill>
      {!isNil(user) && (
        <View row centerVertically paddingTop={0.5} fill>
          <UserImage user={user} className={classNames.avatar} />
          <View className={[classNames.name, classNames.nameCollapsed]}>
            <Text size="body2" numberOfLines={1}>
              {user?.fullName}
            </Text>
          </View>
        </View>
      )}
      {!isNil(date) && <Text size="body2">{formatDateTime(date)}</Text>}
    </View>
  );
}

function UserAndDateExpanded({ user, date, indent }: Props) {
  const isDesktop = useIsDesktop();

  return (
    <View
      row={isDesktop}
      centerVertically
      paddingTop={1}
      paddingLeft={indent ? 7 : 0}
      paddingBottom={1}
      className={isDesktop ? classNames.metadataDesktop : classNames.metadataMobile}
    >
      {!isNil(user) && (
        <View row centerVertically>
          <UserImage user={user} className={classNames.avatar} />
          <View
            className={[
              classNames.name,
              isDesktop ? classNames.nameDesktop : classNames.nameMobile,
            ]}
          >
            <Text size="body2" numberOfLines={1}>
              {user?.fullName}
            </Text>
          </View>
        </View>
      )}
      {!isNil(user) && !isNil(date) && isDesktop && (
        <View paddingHorizontal={1}>
          <Text size="body2" inactive>
            ·
          </Text>
        </View>
      )}
      {!isNil(date) && (
        <Text size="body2" inactive>
          {formatDateTime(date)}
        </Text>
      )}
    </View>
  );
}
